import JSONEditor, { JSONEditorOptions } from 'jsoneditor';
import 'jsoneditor/dist/jsoneditor.css';

import { useEffect, useState } from 'react';
import { DynamicFormDefinition } from 'src/common';
import Ajv from 'ajv';
import { Button, Dialog, DialogActions, DialogContent, IconButton, Tooltip } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import './JsonEditor.scss';
import FormDetailsModal from '../FormDetails/FormDetailsModal';
import Templates from './Templates';

const schema = {
    "title": "DynamicFormDefinition",
    "description": "Object containing dynamic form definition",
    "type": "object",
    "properties": {
        "controls": {
            "items": {
                "$ref": "DynamicFormControl"
            },
            "type": "array"
        },
        // "id": {
        //     "type": "string"
        // },
        // "isOfflineForm": {
        //     "type": "boolean"
        // },
        // "isWebForm": {
        //     "type": "boolean"
        // },
        // "name": {
        //     "type": "string"
        // },
        // "revision": {
        //     "type": "number"
        // },
        // "successMessage": {
        //     "$ref": "SuccessMessage",
        //     "description": "HTML message to display on successful form submission"
        // }
    }
};

const schemaRefs = {
    "ConditionalConfiguration": {
        "properties": {
            "fieldId": {
                "description": "Id of field to be checked for condition",
                "type": "string"
            },
            "value": {
                "description": "Condition that needs to be met in order for item to be displayed",
                "type": "string"
            }
        },
        "type": "object"
    },
    "DateControlSettings": {
        "properties": {
            "isDateTime": {
                "description": "Show date and time pickers",
                "type": "boolean"
            }
        },
        "type": "object"
    },
    "DynamicFormControl": {
        "properties": {
            "autoComplete": {
                "description": "This prop helps users to fill forms faster, especially on mobile devices.\nThe name can be confusing, as it's more like an autofill.\nYou can learn more about it [following the specification](https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill).",
                "type": "string"
            },
            "autoFocus": {
                "description": "If `true`, the `input` element will be focused during the first mount.",
                "type": "boolean"
            },
            "conditionalConfigurations": {
                "description": "Properties for conditionally displaying this item in the form",
                "items": {
                    "$ref": "ConditionalConfiguration"
                },
                "type": "array"
            },
            "customLabel": {
                "description": "If specified will use custom label instead of default control label",
                "type": "boolean"
            },
            "dateControlSettings": {
                "$ref": "DateControlSettings",
                "description": "Configuration specific for date control"
            },
            "defaultValue": {
                "type": "string"
            },
            "disableToggleField": {
                "type": "string"
            },
            "disabled": {
                "description": "If `true`, the `input` element will be disabled.",
                "type": "boolean"
            },
            "fileUploadOptions": {
                "$ref": "DynamicFormUpload",
                "description": "Used for file upload control"
            },
            "format": {
                "description": "Specifies date format",
                "type": "string"
            },
            "fullWidth": {
                "description": "If `true`, the input will take up the full width of its container.",
                "type": "boolean"
            },
            "gridSize": {
                "$ref": "DynamicFormGridSize",
                "description": "Number of grid sections this control should occupy."
            },
            "helpText": {
                "description": "Additional help text to show under form control.",
                "type": "string"
            },
            "id": {
                "description": "The id of the `input` element.\nUse this prop to make `label` and `helperText` accessible for screen readers.",
                "type": "string"
            },
            "inputType": {
                "description": "If using type text, this will specify the type attribute to use on the HTML input field, e.g. 'email', 'password', etc.",
                "type": "string"
            },
            "jsonName": {
                "description": "Property name to use in json output for submitted form",
                "type": "string"
            },
            "label": {
                "type": "string"
            },
            "localization":{
                "type": "object"
            },
            "mask": {
                "anyOf": [
                    {
                        "items": {
                            "anyOf": [
                                {
                                    "$ref": "RegExp"
                                },
                                {
                                    "type": "string"
                                }
                            ]
                        },
                        "type": "array"
                    },
                    {
                        "type": "string"
                    }
                ]
            },
            "maxLength": {
                "description": "Max length of text value",
                "type": "number"
            },
            "maxValue": {
                "description": "Maximum value allowed\nFor date control, can use a date string or special word such as \"today\"",
                "type": "string"
            },
            "minValue": {
                "description": "Minimum value allowed\nFor date control, can use a date string or special word such as \"today\"",
                "type": "string"
            },
            "multiSelect": {
                "description": "Specifies if select control allows multiple selections",
                "type": "boolean"
            },
            "multiline": {
                "description": "If `true`, a textarea element will be rendered instead of an input.",
                "type": "boolean"
            },
            "name": {
                "description": "Name attribute of the `input` element.",
                "type": "string"
            },
            "openTo": {
                "description": "For date controls sets where to open date picker to by default",
                "enum": [
                    "date",
                    "month",
                    "year"
                ],
                "type": "string"
            },
            "placeholder": {
                "description": "The short hint displayed in the input before the user enters a value.",
                "type": "string"
            },
            "regexValidation": {
                "description": "Regex string to validate text input",
                "type": "string"
            },
            "required": {
                "description": "If `true`, the label is displayed as required and the `input` element` will be required.",
                "type": "boolean"
            },
            "restrictRegexCharacters": {
                "description": "If set, will not allow user to enter disallowed characters. If not set, will display validation error when regex validation fails.",
                "type": "boolean"
            },
            "rows": {
                "description": "Number of rows to display when multiline option is set to true.",
                "type": [
                    "string",
                    "number"
                ]
            },
            "rowsMax": {
                "description": "Maximum number of rows to display when multiline option is set to true.",
                "type": [
                    "string",
                    "number"
                ]
            },
            "selectOptions": {
                "anyOf": [
                    {
                        "items": {
                            "type": "string"
                        },
                        "type": "array"
                    },
                    {
                        "items": {
                            "$ref": "SelectOption"
                        },
                        "type": "array"
                    }
                ],
                "description": "Options to be shown for controls of type select and checkbox"
            },
            "selectPropertyOptions": {
                "description": "Options for select/checkbox controls to use when each option corresponds to its own property in json output",
                "items": {
                    "$ref": "PropertyOption"
                },
                "type": "array"
            },
            "selectedIndex": {
                "type": "number"
            },
            "setDisabled": {
                "description": "If disableToggleField is set, this function is used by the form control to update the disabled status",
                "type": "object"
            },
            "setScrollEnabled": {
                "description": "Used to enable or disable form scrolling",
                "type": "object"
            },
            "setState": {
                "description": "Used by form controls to set the state of the parent form",
                "type": "object"
            },
            "size": {
                "description": "The size of the text field.",
                "enum": [
                    "medium",
                    "small"
                ],
                "type": "string"
            },
            "skipSerialization": {
                "description": "Indicates that this control should be skipped when serializing a form submission to JSON",
                "type": "boolean"
            },
            "skipTranslation": {
                "description": "If specified will add the \"notranslate\" CSS class to the control",
                "type": "boolean"
            },
            "state": {
                "anyOf": [
                    {
                        "items": {
                            "type": "string"
                        },
                        "type": "array"
                    },
                    {
                        "type": "string"
                    }
                ]
            },
            "style": {},
            "subGridSize": {
                "$ref": "DynamicFormGridSize",
                "description": "Grid size of the control sub grid (if applicable)"
            },
            "toggleDisabled": {
                "type": "boolean"
            },
            "type": {
                "description": "Form control type. Used to specify component to be used on frontend",
                "enum": [
                    "cancel",
                    "checkbox",
                    "date",
                    "html",
                    "masked",
                    "phone",
                    "radio",
                    "select",
                    "signature",
                    "submit",
                    "text",
                    "upload"
                ],
                "type": "string"
            },
            "value": {
                "type": "string"
            }
        },
        "type": "object"
    },
    "DynamicFormGridSize": {
        "properties": {
            "lg": {
                "$ref": "GridSize"
            },
            "md": {
                "$ref": "GridSize"
            },
            "sm": {
                "$ref": "GridSize"
            },
            "xl": {
                "$ref": "GridSize"
            },
            "xs": {
                "$ref": "GridSize"
            }
        },
        "type": "object"
    },
    "DynamicFormUpload": {
        "properties": {
            "extensionRestrictions": {
                "items": {
                    "type": "string"
                },
                "type": "array"
            },
            "fileAccept": {
                "description": "For file upload, restrict allow file types\nComma separated list of allowable file extensions that will be passed to the 'accept' attribute of the file upload input\nhttps://www.w3schools.com/tags/att_input_accept.asp",
                "type": "string"
            },
            "files": {
                "items": {
                    "$ref": "File"
                },
                "type": "array"
            },
            "multipleFiles": {
                "description": "Allow for multiple files to be uploaded",
                "type": "boolean"
            },
            "setFiles": {
                "description": "Used by form controls to set the file list",
                "type": "object"
            }
        },
        "type": "object"
    },
    "File": {
        "properties": {
            "lastModified": {
                "type": "number"
            },
            "name": {
                "type": "string"
            },
            "size": {
                "type": "number"
            },
            "type": {
                "type": "string"
            },
            "webkitRelativePath": {
                "type": "string"
            }
        },
        "type": "object"
    },
    "GridSize": {
        "enum": [
            1,
            10,
            11,
            12,
            2,
            3,
            4,
            5,
            6,
            7,
            8,
            9,
            "auto"
        ]
    },
    "PropertyOption": {
        "description": "Used to break multi select options into separate properties",
        "properties": {
            "jsonName": {
                "type": "string"
            },
            "label": {
                "anyOf": [
                    {
                        "$ref": "SelectOption"
                    },
                    {
                        "type": "string"
                    }
                ]
            }
        },
        "type": "object"
    },
    "RegExp": {
        "properties": {
            "global": {
                "type": "boolean"
            },
            "ignoreCase": {
                "type": "boolean"
            },
            "lastIndex": {
                "type": "number"
            },
            "multiline": {
                "type": "boolean"
            },
            "source": {
                "type": "string"
            }
        },
        "type": "object"
    },
    "SelectOption": {
        "properties": {
            "label": {
                "type": "string"
            },
            "value": {
                "type": "string"
            }
        },
        "type": "object"
    },
    "SuccessMessage": {
        "properties": {
            "html": {
                "description": "HTML for the message",
                "type": "string"
            },
            "showReturnButton": {
                "description": "If specified, will show a button to return to form",
                "type": "boolean"
            }
        },
        "type": ["object", "null"]
    }
};


const JsonEditor = ({ form, setForm, saveForm, closeForm }: {
    form: DynamicFormDefinition,
    setForm: (form: DynamicFormDefinition) => void,
    saveForm: (form: DynamicFormDefinition) => void,
    closeForm: () => void
}) => {
    const [jsoneditor, setJsoneditor] = useState<JSONEditor>();
    const [container, setContainer] = useState<HTMLDivElement | null>();
    const [ignoreUpdate, setIgnoreUpdate] = useState<boolean>(false);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [confirmSaveOpen, setConfirmSaveOpen] = useState<boolean>(false);
    const [formDetailsOpen, setFormDetailsOpen] = useState<boolean>(false);


    useEffect(() => {
        if (container != null) {
            const ajv = new Ajv({
                allErrors: true,
                verbose: true,
                $data: true,
                jsonPointers: false,
                useDefaults: true
            });

            const options: JSONEditorOptions = {
                mode: 'tree',
                modes: ['tree', 'view', 'form', 'code', 'text', 'preview'],
                schema: schema,
                schemaRefs: schemaRefs,
                allowSchemaSuggestions: true,
                enableSort: false,
                enableTransform: false,
                templates: Templates,
                onChange: () => {
                    const updatedForm = editor.get();

                    if (updatedForm == null) {
                        return;
                    }

                    setHasChanges(true);

                    setIgnoreUpdate(true);
                    const constUpdateForm: DynamicFormDefinition = {
                        id: form.id,
                        isOfflineForm: form.isOfflineForm,
                        isWebForm: form.isWebForm,
                        revision: form.revision,
                        name: form.name,
                        successMessage: form.successMessage,
                        controls: updatedForm.controls
                    };
                    setForm(constUpdateForm);
                },
                ajv: ajv
            };

            const editor = new JSONEditor(container, options);
            const formControls = {
                controls: form.controls
            };
            editor.set(formControls);

            setJsoneditor(editor);
        }
    }, [container]);

    useEffect(() => {
        if (container != null && jsoneditor != null) {

            // Only concerned with updates coming from outside of the editor
            if (ignoreUpdate) {
                setIgnoreUpdate(false);
            } else {
                const formControls = {
                    controls: form.controls
                };
                jsoneditor.update(formControls);
            }
        }
    }, [form]);

    useEffect(() => {
        const handleUnloadEvent = (event: any) => {
            event.returnValue = "Are you sure you want to leave?";
        };

        window.addEventListener("beforeunload", handleUnloadEvent);
      
        return () => window.removeEventListener("beforeunload", handleUnloadEvent);
      }, []);

    const saveClick = () => {
        saveForm(form);
        setHasChanges(false);
    };

    const closeClick = () => {
        if (hasChanges) {
            setConfirmSaveOpen(true);
        } else {
            closeForm();
        }
    };

    const handleCancel = () => {
        setConfirmSaveOpen(false);
    };

    const handleOk = () => {
        closeForm();
    }

    return (
        <div style={{ height: '100%', position: 'relative' }}>
            <div className='top-menu'>
                <Tooltip title="Return to list">
                    <IconButton aria-label="return" className="menu-button" onClick={closeClick}>
                        <ArrowBackIcon />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Save form">
                    <IconButton aria-label="save" className="menu-button" onClick={saveClick}>
                        <SaveIcon />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Edit details">
                    <IconButton aria-label="save" className="menu-button" onClick={() => setFormDetailsOpen(true)}>
                        <SettingsIcon />
                    </IconButton>
                </Tooltip>
            </div>
            <div style={{ height: 'calc(100% - 35px)' }} className="jsoneditor-react-container" ref={elem => setContainer(elem)} />

            <Dialog
                open={confirmSaveOpen}
            >
                <DialogContent>
                    Unsaved changes will be lost. Are you sure you would like to close the form?
                </DialogContent>
                <DialogActions>
                    <Button autoFocus onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button onClick={handleOk}>Yes</Button>
                </DialogActions>
            </Dialog>

            <FormDetailsModal form={form} isOpen={formDetailsOpen} saveForm={() => {}} closeForm={() => setFormDetailsOpen(false)}></FormDetailsModal>
        </div>
    );
};

export default JsonEditor;